import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  SCBottomBarContainer,
  SCButtonWrapper,
  SCButtonCircle,
  SCButtonLabel,
  SCButtonsRow,
  SCButtonIcon,
} from 'components/AugmentedReality/Instructions/BottomBar/styled'
import { FiCamera } from 'react-icons/fi'
import { AugmentedRealityType } from '../../../../types/augmentedReality'

import ModalInfos from 'components/AugmentedReality/Structure/ModalInfos'
import ConfirmModal from 'components/AugmentedReality/Structure/ConfirmModal'

interface BottomBarProps {
  ra: AugmentedRealityType;
  useCamera: boolean;
  onUseCamera: (state: boolean) => void;
  modalOpen: (state: boolean) => void;
  onNext: () => void;
  onPrevious: () => void;
  onCancel: () => void;
}

const BottomBar = ({ ra, useCamera, onUseCamera, modalOpen, onNext, onPrevious, onCancel }: BottomBarProps) => {
  const { t } = useTranslation()
  const [openInfo, setOpenInfo] = useState(false)
  const [openCamera, setOpenCamera] = useState(false)
  const [openError, setOpenError] = useState(false)
  const [animateInfo, setAnimateInfo] = useState(false)

  const triggerAnimate = (setter: (v: boolean) => void) => {
    setter(true)
    setTimeout(() => setter(false), 750)
  }

  const handleGoNextStep = async () => {
    setOpenCamera(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      onUseCamera(true);
      modalOpen(false);
      onNext();
    } catch (err) {
      setOpenError(true);
      modalOpen(true);
    }
  }

  return (
    <>
      <SCBottomBarContainer>
        <SCButtonsRow>
          <SCButtonWrapper onClick={onPrevious}>
            <SCButtonCircle $size="large">
              <SCButtonIcon src={'/static/ra/back.svg'} alt="Retour" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.back')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={() => {
              if (useCamera) {
                handleGoNextStep()
              } else {
                setOpenCamera(true)
                modalOpen(true)
              }
            }}
          >
            <SCButtonCircle $size="xlarge">
              <SCButtonIcon src={'/static/ra/here.svg'} alt="Retour" $size="xlarge" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.instructions.imHere')}</SCButtonLabel>
          </SCButtonWrapper>

          <SCButtonWrapper
            onClick={() => {
              triggerAnimate(setAnimateInfo)
              setOpenInfo(true)
              modalOpen(true)
            }}
          >
            <SCButtonCircle $size="large" $variant="info" className={animateInfo ? 'animate' : ''}>
              <SCButtonIcon src={'/static/ra/infos.svg'} alt="Retour" />
            </SCButtonCircle>
            <SCButtonLabel>{t('augmentedReality.instructions.information')}</SCButtonLabel>
          </SCButtonWrapper>
        </SCButtonsRow>
      </SCBottomBarContainer>

      {openInfo && (
        <ModalInfos
          title={ra.config.infosTitle}
          text={ra.config.infos}
          video={ra.config.infosVideo}
          onClose={() => {
            setOpenInfo(false)
            modalOpen(false)
          }}
        />
      )}

      {openCamera && (
        <ConfirmModal
          title={
            <>
              <FiCamera /> {ra.camera.titleAccess}
            </>
          }
          description={ra.camera.descriptionAccess}
          onCancel={() => {
            setOpenCamera(false)
            modalOpen(false)
            onCancel()
          }}
          onConfirm={handleGoNextStep}
          labelConfirm={t('augmentedReality.iUnderstand')}
          labelCancel={t('augmentedReality.exitExperience')}
        />
      )}

      {!useCamera && openCamera && (
        <ConfirmModal
          title={
            ra.camera.titleAccess !== ''
              ? ra.camera.titleAccess
              : t('augmentedReality.instructions.cameraNeeded')
          }
          description={
            ra.camera.descriptionAccess !== ''
              ? ra.camera.descriptionAccess
              : t('augmentedReality.instructions.cameraNeededDesc')
          }
          onCancel={() => {
            setOpenCamera(false)
            modalOpen(false)
            onCancel()
          }}
          onConfirm={handleGoNextStep}
          labelConfirm={t('augmentedReality.iUnderstand')}
          labelCancel={t('augmentedReality.exitExperience')}
        />
      )}

      {openError && (
        <ConfirmModal
          title={
            ra.camera.titleError !== ''
              ? ra.camera.titleError
              : t('augmentedReality.instructions.cameraError')
          }
          description={
            ra.camera.descriptionError !== '' ? (
              ra.camera.descriptionError
            ) : (
              <>
                <p>{t('augmentedReality.instructions.cameraErrorDesc1')}</p>
                <p>{t('augmentedReality.instructions.cameraErrorDesc2')}</p>
              </>
            )
          }
          onConfirm={() => {
            setOpenError(false)
            modalOpen(false)
          }}
          onCancel={() => {
            setOpenError(false)
            modalOpen(false)
            onCancel()
          }}
          labelConfirm={t('augmentedReality.iUnderstand')}
          labelCancel={t('augmentedReality.exitExperience')}
        />
      )}
    </>
  )
}

export default BottomBar
